import { inject, Injectable } from '@angular/core';
import { createState, select, setProp, Store, withProps } from '@ngneat/elf';
import { getActiveEntity, getActiveId, hasEntity, selectActiveEntity, selectActiveId, selectAllEntities, setActiveId, setEntities, withActiveId, withEntities } from '@ngneat/elf-entities';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { catchError, combineLatest, combineLatestWith, Observable, shareReplay, Subject, switchMap, tap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  AdditionalPageShortDto,
  AddressDetails,
  AddressFormatType,
  Coordinates,
  CoordinatesDto,
  CostRule,
  DeliveryZoneResponse,
  EatinConfiguration,
  FeatureFlag,
  Hours,
  Layout,
  LoyaltyProvider,
  OutletMode,
  SeoData,
  TableSectionDto,
  VisualThemeDto,
  WorkingHours
} from '../../api/v1/models';
import { ConfigDto } from '../../api/v1/models/config-dto';
import { Currency } from '../../api/v1/models/currency';
import { OutletDto } from '../../api/v1/models/outlet-dto';
import { OutletOpenHours } from '../interfaces/outlet-open-hours';
import { PaymentType } from '../../api/v1/models/payment-type';
import { SocialLink } from '../../api/v1/models/social-link';
import { ObjectKeyArray } from '../interfaces/object-key-array.interface';
import { zeroMaskFromTimeInterval } from '../utils/get-time-intervals';
import { SeparateTask } from '../utils/separate-task';
import { SettingsService } from '../../api/v1/services/settings.service';
import { OrderFormStepConfig } from '../interfaces/order-form-step-config.interface';
import { selectIsRequestPending, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { Schedule } from '../interfaces/schedule.interface';
import { DayNamesList } from '../data/day-names';
import { getNowByTZ } from '../utils/convert-timezone';
import { environment } from '../../../environments/environment';

export interface ConfigModel {
  isLoading: boolean,
  settings: ConfigDto | null,
  streets: string[] | null,
  isUserChooseOutlet: boolean,
  activeZoneId: number | null,
  deliveryCoordinates: Coordinates | null,
  activeCostRules: CostRule[] | null,
  costRulesDeliveryDuration: number | null,
  deliveryAvailableIntervals: ObjectKeyArray<WorkingHours> | null,
  userLocale: string | null,
  userPosition: CoordinatesDto | null,
  selectedPaymentType: PaymentType | null,
  mapAPIKey: string | null,
}

const {state, config} = createState(
  withProps<ConfigModel>({
    isLoading: false,
    settings: null,
    streets: null,
    isUserChooseOutlet: false,
    activeCostRules: null,
    costRulesDeliveryDuration: null,
    activeZoneId: null,
    deliveryCoordinates: null,
    deliveryAvailableIntervals: null,
    userLocale: null,
    userPosition: null,
    selectedPaymentType: null,
    mapAPIKey: environment.mapAPIKey || null,
  }),
  withEntities<OutletDto, 'storeId'>({idKey: 'storeId'}),
  withActiveId(),
  withRequestsStatus<'sectionListLoading'>(),
);
export const configStore = new Store({state, name: 'ConfigStore', config});

persistState(configStore, {
  key: 'outletConfigStore',
  storage: localStorageStrategy,
  source: () => configStore.pipe(excludeKeys(['ids', 'entities', 'settings', 'streets', 'isLoading', 'activeCostRules', 'activeZoneId', 'deliveryAvailableIntervals'])),
});

@Injectable({providedIn: 'root'})
export class ConfigRepository {
  private settingsApi = inject(SettingsService);

  constructor() {
    dayjs.extend(utc);
    dayjs.extend(timezone);
  }

  fetchSectionList(): Observable<TableSectionDto[]> {
    configStore.update(updateRequestStatus('sectionListLoading', 'pending'));
    return this.settingsApi.apiV1SettingsSectionsStoreIdGet$Json$Response({
      storeId: this.activeOutletId,
    }).pipe(
      tap(() => configStore.update(updateRequestStatus('sectionListLoading', 'success'))),
      map(response => response.body),
      map(data => {
        if(data.error || !data.result) {
          throw data;
        }
        return data.result;
      }),
      catchError(error => {
        configStore.update(updateRequestStatus('sectionListLoading', 'error', error));
        throw new Error('Section list is not found');
      })
    );
  }

  isSectionListLoading$: Observable<boolean> = configStore.pipe(selectIsRequestPending('sectionListLoading'));

  defaultLocale$: Observable<string> = configStore.pipe(
    select(state => state.settings?.defaultLocale),
    filter((defaultLocale: string | undefined): defaultLocale is string => !!defaultLocale)
  );

  loyaltyAuthProvider$: Observable<LoyaltyProvider> = configStore.pipe(
    select(state => state.settings?.loyaltySettings.provider || LoyaltyProvider.None),
  );

  public isMainEnvironment$: Observable<boolean | null> = configStore.pipe(select(state => {
    if(!state.settings) {
      return null;
    }
    return state.settings.defaultLocale === 'ru';
  }));

  public releasedFeatures$: Observable<FeatureFlag[]> = configStore.pipe(select(state => state.settings?.featureFlags || []));

  public isLoading$: Observable<boolean> = configStore.pipe(select(state => state.isLoading));
  public isUserChooseOutlet$: Observable<boolean> = configStore.pipe(select(state => state.isUserChooseOutlet));

  public isHasSettings$: Observable<boolean> = configStore.pipe(select(state => !!state.settings));
  public logoUrl$: Observable<string | undefined | null> = configStore.pipe(select(state => state.settings?.theme?.logoImageUrl));
  public bannerUrls$: Observable<{ [key: string]: string }[] | undefined> = configStore.pipe(
    select(state => state.settings?.theme?.bannerButtonImages),
  );
  public seoData$: Observable<SeoData | null> = configStore.pipe(select(state => state.settings?.seoData ?? null));
  public sharingImageUrls$: Observable<string | undefined> = configStore.pipe(select(state => state.settings?.theme?.sharingImageUrl));
  theme$: Observable<VisualThemeDto | undefined> = configStore.pipe(select(state => state.settings?.theme));

  public locale$: Observable<string | undefined | null> = configStore.pipe(select(state => state.settings?.locale));
  public socialLinks$: Observable<SocialLink[] | null> = configStore.pipe(select(state => {
    if(state.settings && state.settings.socialLinks.length) {
      return state.settings.socialLinks;
    }
    return null;
  }));

  loadSettingsSubj$ = new Subject<unknown>();

  loadedSettings$: Observable<ConfigDto | null> = this.loadSettingsSubj$.asObservable().pipe(
    switchMap(() => configStore.pipe(select(state => state.settings))),
    shareReplay({refCount: false, bufferSize: 1}),
  );

  layout$: Observable<Layout> = this.loadedSettings$.pipe(
    map(settings => settings?.frontConfig.layout || Layout.Fresh),
    shareReplay({refCount: false, bufferSize: 1}),
  );

  public paymentsTypes$: Observable<PaymentType[] | undefined | null> = configStore.pipe(
    select(state => state.settings?.paymentTypes),
    map(paymentTypes => paymentTypes && paymentTypes.length ? paymentTypes : null)
  );

  public get activePaymentsType(): PaymentType {
    return configStore.query(
      state => state.settings && state.settings.paymentTypes ? state.settings?.paymentTypes[0] : PaymentType.Cod
    );
  }

  public googleAnalyticsCodes$: Observable<string[]> = configStore.pipe(select(state => {
    if(!state.settings || state.settings.defaultLocale === 'ru') {
      return [];
    }
    return state.settings.analyticsConfig?.googleAnalyticsCodes || null;
  }));

  public googleTagManagerCodes$: Observable<string[]> = configStore.pipe(select(state => {
    if(!state.settings || state.settings.defaultLocale === 'ru') {
      return [];
    }
    return state.settings.analyticsConfig?.googleTagManagerCodes || null;
  }));

  public yAnalyticsCodes$: Observable<string[] | null> = configStore.pipe(select(state => {
    return state.settings?.analyticsConfig?.yAnalyticsCodes || null;
  }));

  additionalPagesNew$: Observable<{ [key: string]: AdditionalPageShortDto[] } | null> = configStore.pipe(
    select(state => state.settings?.additionalPages || null)
  );

  public additionalAbout$: Observable<AdditionalPageShortDto | undefined> = configStore.pipe(
    select(state => {
      const additionalPages = state.settings?.additionalPages;

      if(!additionalPages || !additionalPages['about']) {
        return;
      }

      return additionalPages['about'][0] || undefined;
    })
  );
  public additionalPrivacy$: Observable<AdditionalPageShortDto | undefined> = configStore.pipe(
    select(state => {
      const additionalPages = state.settings?.additionalPages;

      if(!additionalPages || !additionalPages['privacy_policy']) {
        return;
      }

      return additionalPages['privacy_policy'][0] || undefined;
    })
  );
  personalDataPolicyPage$ = configStore.pipe(
    select(state => state.settings?.additionalPages['personal_data_policy']?.[0] ?? null)
  );
  personalDataConsentPage$ = configStore.pipe(
    select(state => state.settings?.additionalPages['personal_data_consent']?.[0] ?? null)
  );

  public additionalPages$: Observable<AdditionalPageShortDto[] | undefined> = configStore.pipe(
    select(state => {
      const additionalPages = state.settings?.additionalPages;

      if(!additionalPages || !additionalPages['page']) {
        return;
      }

      return additionalPages['page'];
    }),
    map(pages => pages?.sort((a, b) => a.priority - b.priority))
  );

  public orderIntervalInMinutes$: Observable<number> = configStore.pipe(select(state => state.settings?.deliveryRestrictions?.orderIntervalInMinutes || 15));

  get orderIntervalInMinutes(): number {
    return configStore.query(state => state.settings?.deliveryRestrictions?.orderIntervalInMinutes || 15);
  }

  public outlets$: Observable<OutletDto[]> = configStore.pipe(selectAllEntities());

  public activeOutlet$: Observable<OutletDto> = configStore.pipe(
    selectActiveEntity(),
    filter((outlet): outlet is OutletDto => !!outlet),
    shareReplay({refCount: false, bufferSize: 1}),
  );
  public activeOutletId$: Observable<number | undefined> = configStore.pipe(selectActiveId());

  activeOutletCoordinates$: Observable<Coordinates | null> = this.activeOutlet$.pipe(
    map((outlet) => outlet.coordinates || null),
  );

  public get activeOutlet(): OutletDto | undefined {
    return configStore.query(getActiveEntity());
  }

  public get activeOutletId(): number {
    return configStore.query(getActiveId);
  }

  activeOutletCity$: Observable<string> = this.activeOutlet$.pipe(
    map(outlet => outlet.addressDetails?.city || this.getOutletCityFromString(outlet))
  );

  outletsCityMap$: Observable<{ [key: string]: OutletDto[] }> = this.outlets$.pipe(
    map(outlets => {
      const groupedOutlets: { [key: string]: OutletDto[] } = {};

      outlets.forEach((outlet => {
        const outletCity: string = outlet.addressDetails?.city || this.getOutletCityFromString(outlet);
        if(!groupedOutlets[outletCity]) {
          groupedOutlets[outletCity] = [];
        }
        groupedOutlets[outletCity].push(outlet);
      }));
      return groupedOutlets;
    }),
    shareReplay({refCount: true, bufferSize: 1}),
  );

  sortOutletsCityMap$: Observable<{ [key: string]: OutletDto[] }> = this.outletsCityMap$.pipe(
    map(outletGroups => {
      Object.keys(outletGroups).forEach(key => {
        outletGroups[key].sort((a, b) => (a.addressDetails?.city || '') > (b.addressDetails?.city || '') ? 1 : -1);
      });

      return outletGroups;
    }),
    shareReplay({refCount: true, bufferSize: 1}),
  );

  private getOutletCityFromString(outlet: OutletDto): string {
    return outlet.address.split(',')[0];
  }

  fromRussia$: Observable<boolean> = this.activeOutlet$.pipe(
    map(outlet => ['Russia', 'Россия'].includes(outlet.country ?? ''))
  );

  public addressFieldSettings$ = this.activeOutlet$.pipe(
    map(outlet => {
      return {
        usesIndexSearch: outlet.addressFormatType === AddressFormatType.International,
        useUaeAddressingSystem: outlet.addressFormatType === AddressFormatType.Intnopostcode,
        useAddressFormatType: outlet.addressFormatType === AddressFormatType.City,
      };
    })
  );

  pickupDurationInMinutes$: Observable<number> = this.activeOutlet$.pipe(
    map((outlet) => outlet.pickupDurationInMinutes || 0),
  );

  get addressFieldSettings(): { useUaeAddressingSystem: boolean; usesIndexSearch: boolean } | null {
    const outlet = configStore.query(getActiveEntity());
    if(!outlet) {
      return null;
    }
    return {
      usesIndexSearch: outlet.addressFormatType === AddressFormatType.International,
      useUaeAddressingSystem: outlet.addressFormatType === AddressFormatType.Intnopostcode,
    };
  }

  get restoTimeZone(): string {
    const outlet = configStore.query(getActiveEntity());
    return outlet?.timeZone || 'Europe/Moscow';
  }

  public address$: Observable<string | null> = this.activeOutlet$.pipe(map(outlet => {
    if(outlet.addressDetails) {
      return `${outlet.addressDetails.streetLine}, ${outlet.addressDetails.city}`;
    }
    return outlet.address ?? null;
  }));
  addressDetails$: Observable<AddressDetails | null> = this.activeOutlet$.pipe(map(outlet => outlet.addressDetails ?? null));
  public phone$: Observable<string | null> = this.activeOutlet$.pipe(map(outlet => outlet.phone ?? null));

  public get phone(): string | null {
    return configStore.query(getActiveEntity())?.phone ?? null;
  }

  outletMode$: Observable<OutletMode | null> = this.activeOutlet$.pipe(map(outlet => outlet.mode ?? null));

  isEatinMode$: Observable<boolean> = this.outletMode$.pipe(
    map(outletMode => outletMode === OutletMode.Eatin)
  );

  public useCoupons$: Observable<boolean> = configStore.pipe(
    select(state => state.settings?.loyaltySettings?.useCoupons ?? false)
  );

  public isLoyaltyUseBonuses$: Observable<boolean> = configStore.pipe(
    select(state => state.settings?.loyaltySettings?.useBonusPayment ?? false)
  );

  public isLoyalty$: Observable<boolean> = configStore.pipe(
    select(state => state.settings?.loyaltySettings?.useLoyalty ?? false)
  );

  public useBonusPayment$: Observable<boolean> = this.isLoyaltyUseBonuses$;

  eatinConfig$: Observable<EatinConfiguration | null> = configStore.pipe(
    select(state => state.settings?.eatinConfig || null)
  );

  selectedPaymentType$: Observable<PaymentType | null> = configStore.pipe(select(state => state.selectedPaymentType));

  setSelectedPaymentType(type: PaymentType | null): void {
    configStore.update(setProp('selectedPaymentType', type));
  }

  customerStepConfig$: Observable<EatinConfiguration> = combineLatest([
    this.eatinConfig$,
    this.fromRussia$,
    this.isEatinMode$,
    this.selectedPaymentType$,
  ]).pipe(
    map(([config, fromRussia, isEatinMode, selectedPaymentType]) => {
      const stepConfig = config ? {...config} : {
        useCustomerEmail: true,
        useCustomerPhone: true,
        useCustomerName: true,
      };

      if(!stepConfig.useCustomerEmail && fromRussia && isEatinMode) {
        stepConfig.useCustomerEmail = selectedPaymentType === PaymentType.Web;
      }

      return stepConfig;
    })
  );

  orderFormStepConfig$: Observable<OrderFormStepConfig> = combineLatest([
    this.activeOutlet$,
    this.useCoupons$,
    this.customerStepConfig$,
  ]).pipe(
    map(([outlet, useCoupons, customerStepConfig]) => {
      const isEatin = outlet.mode === OutletMode.Eatin;

      let isShowCustomerStep = true;

      if(customerStepConfig) {
        isShowCustomerStep = customerStepConfig.useCustomerName || customerStepConfig.useCustomerPhone || customerStepConfig.useCustomerEmail;
      }

      return {
        isShowTableStep: isEatin,
        isShowCustomerStep: isShowCustomerStep,
        isShowDeliveryTypeStep: !isEatin,
        isShowAddressStep: !isEatin,
        isShowExpectedDateTimeStep: !isEatin,
        isShowOrderPaymentStep: true,
        isShowNotesStep: !isEatin,
        isShowCouponStep: !isEatin && useCoupons,
      };
    })
  );

  public currency$: Observable<Currency | null> = this.activeOutlet$.pipe(map(outlet => outlet?.currency ?? null)).pipe(
    shareReplay({refCount: true, bufferSize: 1}),
  );

  public isProvidingRestoSelection$: Observable<boolean> = this.outlets$.pipe(map(outlets => outlets.length > 1));
  public isProvidingCollection$: Observable<boolean> = this.activeOutlet$.pipe(map(outlet => {
    if(outlet?.mode) {
      return [OutletMode.PickupDelivery, OutletMode.Pickup].includes(outlet.mode);
    }
    return outlet?.providesCollection || false;
  }));
  public isProvidingDelivery$: Observable<boolean> = this.activeOutlet$.pipe(map(outlet => {
    if(outlet?.mode) {
      return [OutletMode.PickupDelivery, OutletMode.Delivery].includes(outlet.mode);
    }
    return outlet?.providesDelivery || false;
  }));
  public isProvidingEatIn$: Observable<boolean> = this.activeOutlet$.pipe(map(outlet => {
    return outlet?.mode === OutletMode.Eatin || false;
  }));

  public activeZoneId$: Observable<number | null> = configStore.pipe(select(state => state.activeZoneId));

  costRulesDeliveryDuration$: Observable<number | null> = configStore.pipe(
    select((state) => state.costRulesDeliveryDuration),
  );

  activeCostRules$: Observable<CostRule[] | null> = configStore.pipe(
    select((state) => state.activeCostRules),
    shareReplay({refCount: false, bufferSize: 1}),
  );
  deliveryAvailableIntervals$: Observable<ObjectKeyArray<WorkingHours> | null> = configStore.pipe(select(state => state.deliveryAvailableIntervals));
  pickupAvailableIntervals$: Observable<ObjectKeyArray<WorkingHours> | null> = this.activeOutlet$.pipe(map(outlet => outlet?.pickupIntervals || null));

  deliveryDurationInMinutes$: Observable<number> = this.costRulesDeliveryDuration$.pipe(map(duration => duration ?? 0));

  public isDeliveryOrCollection$: Observable<boolean> = combineLatest([
    this.isProvidingDelivery$,
    this.isProvidingCollection$
  ]).pipe(
    map(([isProvidesDelivery, isProvidesCollection]) => {
        return isProvidesDelivery || isProvidesCollection;
      }
    ));

  public isBasketMode$: Observable<boolean> = combineLatest([
    this.isDeliveryOrCollection$,
    this.isProvidingEatIn$,
    this.activeOutlet$,
  ]).pipe(
    map(([isDeliveryOrCollection, isProvidesEatIn]) => {
        return isDeliveryOrCollection || isProvidesEatIn;
      }
    ));

  @SeparateTask()
  setConfig(settings: ConfigDto | null): void {
    if(settings && settings.outlets && settings.outlets.length > 0) {
      this.setOutlets(settings.outlets);
    }

    configStore.update(state => ({
      ...state,
      settings: settings
    }));

    this.loadSettingsSubj$.next(null);
  }

  @SeparateTask()
  setLoadingState(isLoading: boolean): void {
    configStore.update(state => ({
      ...state,
      isLoading: isLoading
    }));
  }

  public workingHours$: Observable<OutletOpenHours | null> = this.activeOutlet$.pipe(
    map(activeOutlet => {
      if(!activeOutlet || !Object.keys(activeOutlet.workingIntervals).length) {
        return null;
      }

      const currentDay = Intl.DateTimeFormat('en', {weekday: 'long'}).format(new Date());
      const [dayHours] = activeOutlet.workingIntervals[currentDay] ?? [];

      if(!dayHours) {
        return null;
      }

      return {
        open: zeroMaskFromTimeInterval(dayHours.open),
        close: zeroMaskFromTimeInterval(dayHours.close),
        day: Intl.DateTimeFormat('en', {weekday: 'long'}).format(new Date()),
      };
    })
  );

  setOutlets(outlets: OutletDto[]): void {
    configStore.update(setEntities(outlets));

    const ids = configStore.query(state => state.ids);
    const activeId = configStore.query(getActiveId);

    if(!activeId || !ids.includes(activeId)) {
      let activeOutlet = outlets.find(outlet => outlet.providesDelivery && outlet.providesCollection);
      if(!activeOutlet) {
        activeOutlet = outlets.find(outlet => outlet.providesDelivery || outlet.providesCollection);
      }

      if(!activeOutlet) {
        activeOutlet = outlets[0];
      }

      this.setActiveOutlet(activeOutlet.storeId);
    }
  }

  setUserChooseState(isUserChooseOutlet: boolean): void {
    configStore.update(state => ({
      ...state,
      isUserChooseOutlet: isUserChooseOutlet,
    }));
  }

  setActiveOutlet(id: number): void {
    const activeId = this.activeOutletId;
    if(activeId == id) {
      return;
    }

    const outletExists = configStore.query(hasEntity(id));
    if(!outletExists) {
      return;
    }
    configStore.update(setActiveId(id));
  }

  get activeZoneId(): number | null {
    return configStore.query(state => state.activeZoneId);
  }

  get coordinates(): Coordinates | null {
    return configStore.query(state => state.deliveryCoordinates);
  }

  setActiveDeliveryZone(deliveryZone: DeliveryZoneResponse | null): void {
    const initialCostRules = !deliveryZone ? null : this.getNearestInterval(deliveryZone.workIntervals);

    configStore.update(state => ({
      ...state,
      activeZoneId: deliveryZone ? deliveryZone.deliveryZoneId : null,
      deliveryCoordinates: deliveryZone ? deliveryZone.coordinates : null,
      deliveryAvailableIntervals: deliveryZone ? deliveryZone.workIntervals : null,
      activeCostRules: initialCostRules ? initialCostRules.costRule : null,
      costRulesDeliveryDuration: initialCostRules ? initialCostRules.deliveryDuration : null,
    }));
  }

  setActiveCostRules(rules: CostRule[] | null): void {
    configStore.update(state => ({
      ...state,
      activeCostRules: rules
    }));
  }

  getOutletSchedule(workingIntervals: { [key: string]: WorkingHours[] }): Schedule | null {
    let schedule: Schedule | null = null;
    const currentDayName: string = new Intl.DateTimeFormat('en-US', {weekday: 'long'}).format(new Date());

    if(!workingIntervals[currentDayName]) {
      return null;
    }

    const dayHours = workingIntervals[currentDayName];
    schedule = {
      dayKey: currentDayName,
      open: zeroMaskFromTimeInterval(dayHours.at(0)?.open),
      close: zeroMaskFromTimeInterval(dayHours.at(-1)?.close),
      isOpen: this.getOpenStateOutlet(dayHours),
    };

    return schedule;
  }

  getOpenStateOutlet(hours: Hours[]): boolean {
    const firstHour = hours[0];
    const lastHour = hours[hours.length - 1];
    const now = getNowByTZ(this.restoTimeZone);

    if(!firstHour || !lastHour) {
      return false;
    }
    if(!firstHour.open || !lastHour.close) {
      return false;
    }

    const openTime: number = firstHour.open.hour * 60 + firstHour.open.minute;
    const closeTime: number = lastHour.close.hour * 60 + lastHour.close.minute;

    const currentTime = now.getHours() * 60 + now.getMinutes();

    if(openTime < closeTime) {
      return currentTime > openTime && currentTime < closeTime;
    }

    return currentTime > openTime ? true : currentTime < closeTime;
  }

  userLocale$: Observable<string | null> = configStore.pipe(select(state => state.userLocale));

  get userLocale(): string | null {
    return configStore.query(state => state.userLocale);
  }

  setUserLocale(locale: string): void {
    configStore.update(setProp('userLocale', locale));
  }

  currentLocale$: Observable<string> = this.userLocale$.pipe(
    combineLatestWith(this.defaultLocale$),
    map(([userLocale, defaultLocale]) => userLocale ? userLocale : defaultLocale)
  );

  promotionsCountByLang$: Observable<{ [key: string]: number }> = configStore.pipe(
    select(state => state.settings?.promotions || null),
    filter((promotionsCounts): promotionsCounts is { [key: string]: number } => !!promotionsCounts)
  );

  promotionCount$: Observable<number> = this.promotionsCountByLang$.pipe(
    combineLatestWith(this.currentLocale$),
    map(([promotionsCountByLang, currentLocale]) => {
      if(promotionsCountByLang[currentLocale]) {
        return promotionsCountByLang[currentLocale];
      }

      return 0;
    })
  );

  userPosition$: Observable<CoordinatesDto | null> = configStore.pipe(
    select(state => state.userPosition)
  );

  setUserPosition(position: CoordinatesDto | null): void {
    configStore.update(setProp('userPosition', position));
  }

  mapAPIKey$: Observable<string | null> = configStore.pipe(select(state => state.mapAPIKey));

  isMapInFeatures$: Observable<boolean> = this.releasedFeatures$.pipe(
    filter(features => features.includes(FeatureFlag.Maps)),
    map(() => true)
  );

  getNearestInterval(workingHours: { [key: string]: WorkingHours[] }): { costRule: CostRule[], deliveryDuration: number, activeAt: number } | null {
    const restoTimeZone = this.restoTimeZone ?? 'Europe/Moscow';
    const workingDays: Set<string> = new Set(Object.keys(workingHours));
    const now = dayjs();

    const normalizedIndex = (now.tz(restoTimeZone).day() + 6) % 7;

    for(let i = 0; i < 7; i++) {
      const nextDayIndex: number = (normalizedIndex + i) % 7;
      const nextDay: string = DayNamesList[nextDayIndex];

      if(!workingDays.has(nextDay)) {
        continue;
      }

      for(const interval of workingHours[nextDay]) {
        const openTime = now.tz(restoTimeZone).add(i, 'day').hour(interval.open.hour).minute(interval.open.minute);
        const closeTime = now.tz(restoTimeZone).add(i, 'day').hour(interval.close.hour).minute(interval.close.minute);

        if(openTime > closeTime || closeTime >= now) {
          return {
            activeAt: openTime < now ? 0 : openTime.valueOf(),
            deliveryDuration: interval.deliveryDuration,
            costRule: interval.costRules,
          };
        }
      }
    }

    return null;
  }
}
